import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Code, Lock, Zap, Server, Database, Globe } from 'lucide-react';

export default function APIDocumentation() {
  const navigate = useNavigate();

  const endpoints = [
    {
      method: 'POST',
      path: '/api/auth/login',
      description: 'Authenticate user and get access token',
      public: true
    },
    {
      method: 'GET',
      path: '/api/health',
      description: 'Check API and database health status',
      public: true
    },
    {
      method: 'GET',
      path: '/api/tenant/plans',
      description: 'Get available subscription plans',
      public: true
    },
    {
      method: 'POST',
      path: '/api/tenant/register',
      description: 'Register a new tenant/company',
      public: true
    },
    {
      method: 'GET',
      path: '/api/tenant/check-subdomain/{subdomain}',
      description: 'Check if subdomain is available',
      public: true
    },
    {
      method: 'GET',
      path: '/api/bookings',
      description: 'Get bookings for authenticated tenant',
      public: false
    },
    {
      method: 'POST',
      path: '/api/bookings',
      description: 'Create a new booking',
      public: false
    },
    {
      method: 'GET',
      path: '/api/therapists',
      description: 'Get therapists for authenticated tenant',
      public: false
    },
    {
      method: 'GET',
      path: '/api/analytics/dashboard',
      description: 'Get dashboard analytics',
      public: false
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      {/* Header */}
      <nav className="fixed top-0 w-full z-40 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/welcome')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 pt-24 pb-16">
        <div className="flex items-center gap-3 mb-4">
          <Code className="w-8 h-8 text-teal-500" />
          <h1 className="text-3xl font-bold text-white">API Documentation</h1>
        </div>
        
        <p className="text-slate-400 mb-8 max-w-2xl">
          WellNest Technologies provides a RESTful API for integrating spa management capabilities into your applications.
        </p>

        {/* Overview Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-6">
              <Server className="w-10 h-10 text-teal-500 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Base URL</h3>
              <code className="text-sm bg-slate-800 px-3 py-1 rounded text-teal-400">
                https://api.wellnesttechnologies.com
              </code>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-6">
              <Lock className="w-10 h-10 text-teal-500 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Authentication</h3>
              <p className="text-slate-400 text-sm">Bearer token via Authorization header</p>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-6">
              <Zap className="w-10 h-10 text-teal-500 mb-4" />
              <h3 className="text-lg font-semibold text-white mb-2">Rate Limits</h3>
              <p className="text-slate-400 text-sm">1000 requests/hour per API key</p>
            </CardContent>
          </Card>
        </div>

        {/* Authentication Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Authentication</h2>
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-6">
              <p className="text-slate-300 mb-4">
                All authenticated endpoints require a Bearer token in the Authorization header:
              </p>
              <div className="bg-slate-800 p-4 rounded-lg font-mono text-sm">
                <span className="text-slate-500">Authorization:</span> <span className="text-teal-400">Bearer</span> <span className="text-yellow-400">{"<your-access-token>"}</span>
              </div>
              <p className="text-slate-400 mt-4 text-sm">
                Obtain your access token by calling the <code className="text-teal-400">/api/auth/login</code> endpoint with valid credentials.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Endpoints Section */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">API Endpoints</h2>
          <div className="space-y-4">
            {endpoints.map((endpoint, index) => (
              <Card key={index} className="bg-slate-900 border-slate-700">
                <CardContent className="py-4">
                  <div className="flex items-center gap-4 flex-wrap">
                    <Badge className={`${
                      endpoint.method === 'GET' ? 'bg-blue-600' :
                      endpoint.method === 'POST' ? 'bg-green-600' :
                      endpoint.method === 'PUT' ? 'bg-yellow-600' :
                      'bg-red-600'
                    } text-white font-mono`}>
                      {endpoint.method}
                    </Badge>
                    <code className="text-teal-400 font-mono">{endpoint.path}</code>
                    {endpoint.public ? (
                      <Badge variant="outline" className="border-green-500 text-green-400">Public</Badge>
                    ) : (
                      <Badge variant="outline" className="border-yellow-500 text-yellow-400">
                        <Lock className="w-3 h-3 mr-1" /> Auth Required
                      </Badge>
                    )}
                  </div>
                  <p className="text-slate-400 mt-2 ml-1">{endpoint.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Example Request */}
        <section className="mb-12">
          <h2 className="text-2xl font-bold text-white mb-6">Example Request</h2>
          <Card className="bg-slate-900 border-slate-700">
            <CardHeader>
              <CardTitle className="text-lg text-white">Get Subscription Plans</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-slate-800 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                <pre className="text-slate-300">{`curl -X GET "https://api.wellnesttechnologies.com/api/tenant/plans" \\
  -H "Content-Type: application/json"`}</pre>
              </div>
              <p className="text-slate-400 mt-4 mb-2">Response:</p>
              <div className="bg-slate-800 p-4 rounded-lg font-mono text-sm overflow-x-auto">
                <pre className="text-slate-300">{`{
  "plans": [
    {
      "id": "starter_monthly",
      "name": "Starter",
      "price": 999,
      "billing_cycle": "monthly",
      "features": ["3 locations", "10 therapists", "Basic analytics"]
    }
  ]
}`}</pre>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Contact */}
        <section>
          <h2 className="text-2xl font-bold text-white mb-6">Need Help?</h2>
          <Card className="bg-slate-900 border-slate-700">
            <CardContent className="pt-6">
              <p className="text-slate-300 mb-4">
                For API access, custom integrations, or technical support:
              </p>
              <div className="flex flex-wrap gap-4">
                <Button variant="outline" className="border-teal-500 text-teal-400 hover:bg-teal-500/10">
                  api@wellnesttechnologies.com
                </Button>
                <Button variant="outline" className="border-slate-600 text-slate-300">
                  +91 8800977146
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  );
}
